import React, { useState, useEffect } from "react";
import AuthService from "../../../services/auth.service";
import AccountService from "../../../services/account.service";
import { updateUserInformationPayload } from "../../../interfaces/account.interface";

interface UserData {
  fname: string;
  lname: string;
  username: string;
  email: string;
  company: string;
  contact: string;
  address: string;
  country: string;
  state: string;
  city: string;
  zip: string;
}

const UserInfo: React.FC = () => {
  const [userData, setUserData] = useState<UserData>({
    fname: "",
    lname: "",
    username: "",
    email: "",
    company: "",
    contact: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zip: "",
  });
  const [editedData, setEditedData] = useState<UserData>(userData);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof UserData, string>>>(
    {}
  );
  const [initialErrors, setInitialErrors] = useState<
    Partial<Record<keyof UserData, string>>
  >({});
  const [modifiedFields, setModifiedFields] = useState<Set<keyof UserData>>(
    new Set()
  );

  useEffect(() => {
    fetchUserData();
  }, []);

  const validateField = (name: keyof UserData, value: string) => {
    if (!value || value.trim() === "") {
      return "This field is required!";
    }
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }
    if (name === "contact") {
      const phoneRegex = /^\+?[\d\s-]{10,}$/;
      if (!phoneRegex.test(value)) {
        return "Please enter a valid phone number";
      }
    }
    if (name === "zip") {
      const zipRegex = /^\d{5}(-\d{4})?$/;
      if (!zipRegex.test(value)) {
        return "Please enter a valid ZIP code (ex. 12345 or 12345-6789)";
      }
    }
    return "";
  };

  const validateAllFields = (data: UserData) => {
    const newErrors: Partial<Record<keyof UserData, string>> = {};
    Object.keys(data).forEach((field) => {
      const fieldName = field as keyof UserData;
      if (fieldName !== "company") {
        const error = validateField(fieldName, data[fieldName]);
        if (error) {
          newErrors[fieldName] = error;
        }
      }
    });
    return newErrors;
  };

  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const response = await AuthService.getUserData();
      if (response.data) {
        const newData = {
          userId: response.data.id || "",
          fname: response.data.firstName || "",
          lname: response.data.lastName || "",
          username: response.data.username || "",
          email: response.data.email || "",
          company: response.data.organizationName || "",
          contact: response.data.contactNo || "",
          address: response.data.address || "",
          country: response.data.country || "",
          state: response.data.state || "",
          city: response.data.city || "",
          zip: response.data.zipcode || "",
        };
        const currentErrors = validateAllFields(newData);
        setUserData(newData);
        setEditedData(newData);
        setModifiedFields(new Set());
        setErrors(currentErrors);
        setInitialErrors(currentErrors);
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log(errors);
    const { name, value } = e.target;
    const fieldName = name as keyof UserData;
    const error = validateField(fieldName, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fieldName] = error;
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });
    setEditedData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (value !== userData[fieldName]) {
      setModifiedFields((prev) => new Set(prev).add(fieldName));
    } else {
      setModifiedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldName);
        return newSet;
      });
    }
  };

  const handleEdit = () => {
    setInitialErrors({ ...errors });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setModifiedFields(new Set());
    setEditedData(userData);
    setErrors(initialErrors);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const newErrors: Partial<Record<keyof UserData, string>> = {};
    let hasErrors = false;
    modifiedFields.forEach((field) => {
      const error = validateField(field, editedData[field]);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    try {
      const fieldMapping: Record<
        keyof UserData,
        keyof updateUserInformationPayload
      > = {
        fname: "firstName",
        lname: "lastName",
        username: "username",
        email: "email",
        contact: "contactNo",
        company: "company",
        address: "address",
        country: "country",
        state: "state",
        city: "city",
        zip: "zipCode",
      };

      const updates = Array.from(modifiedFields).reduce((acc, field) => {
        const apiField = fieldMapping[field];
        if (apiField) {
          return {
            ...acc,
            [apiField]: editedData[field],
          };
        }
        return acc;
      }, {});

      await AccountService.updateUserInformation(
        updates as updateUserInformationPayload
      );
      if (modifiedFields.has("username")) {
        localStorage.setItem('uname', editedData.username);
      }
      setUserData((prev) => ({
        ...prev,
        ...editedData,
      }));
      setModifiedFields(new Set());
      setIsEditing(false);
      setErrors({});
      setInitialErrors({});
    } catch (error) {
      console.error("Error saving user information changes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = () => {
    const changes =
      modifiedFields.size > 0 &&
      Array.from(modifiedFields).some(
        (field) => editedData[field] !== userData[field]
      );
    return changes;
  };

  const renderField = (label: string, name: keyof UserData) => {
    const value = isEditing ? editedData[name] : userData[name];
    const error = errors[name];
    const needsVerification = name === "email" || name === "username";

    return (
      <div className="field-container">
        <div className="field-label">{label}</div>
        {isEditing && name !== "company" && name !== "email" ? (
          <div className="field-input">
            <input
              type="text"
              name={name}
              value={value}
              onChange={handleChange}
              className={error ? "error" : ""}
              disabled={isLoading}
            />
            {error ? (
              <div className="error-message">{error}</div>
            ) : (
              needsVerification && (
                <div className="verification-warning">
                  Refresh the page for changes to be reflected
                </div>
              )
            )}
          </div>
        ) : (
          <div className={`field-value ${error ? "invalid-field" : ""}`}>
            {value ||
              "Please fill out this field! Press edit to insert information"}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-info-root">
      <div className="user-info-content">
        <div className="section-header">
          <h2>Personal Information</h2>
          {isEditing ? (
            <div className="button-group">
              <button
                className="widget-button"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="widget-button-special"
                onClick={handleSave}
                disabled={
                  isLoading || Object.keys(errors).length > 0 || !hasChanges()
                }
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
            </div>
          ) : (
            <button
              className="widget-button"
              onClick={handleEdit}
              disabled={isLoading}
            >
              Edit
            </button>
          )}
        </div>

        <div className="info-section">
          {renderField("First Name", "fname")}
          {renderField("Last Name", "lname")}
          {renderField("Email", "email")}
          {renderField("Username", "username")}
          {renderField("Contact Number", "contact")}
          {renderField("Company Name", "company")}
        </div>

        <h2>Address Information</h2>
        <div className="info-section">
          {renderField("Address", "address")}
          {renderField("Country", "country")}
          {renderField("State", "state")}
          {renderField("City", "city")}
          {renderField("ZIP Code", "zip")}
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
